/**
 * The two things every consumer needs once it holds an `OnlineGame`: where that
 * game lives on the web, and what to call it in front of a human.
 *
 * `OnlineGame` is a WebSocket namespace slug. For every game but one the slug
 * is also the site subpath, but backgammon is served from `/long-backgammon/`
 * — so "slug === path" is a trap that only breaks on one value. Both maps live
 * beside the union that keys them, exhaustively typed, so adding a game is a
 * compile error here rather than a dead link there.
 *
 * Labels are English-only, matching the decision recorded for the rest of F5's
 * authenticated surfaces: this package has no i18n dependency and its consumers
 * (landing's /friends page, the in-game invite banner) are not localized.
 */

import type { OnlineGame } from './types.js';

const GAME_WEB_PATHS: Readonly<Record<OnlineGame, string>> = {
  backgammon: 'long-backgammon',
  checkers: 'checkers',
  domino: 'domino',
  mahjong: 'mahjong',
  chess: 'chess',
};

const GAME_LABELS: Readonly<Record<OnlineGame, string>> = {
  backgammon: 'Long Backgammon',
  checkers: 'Checkers',
  domino: 'Dominoes',
  mahjong: 'Mahjong',
  chess: 'Chess',
};

/** The game's path segment on the combined deploy — no surrounding slashes. */
export function gameWebPath(game: OnlineGame): string {
  return GAME_WEB_PATHS[game];
}

/** The game's display name, as the landing page titles it. */
export function gameLabel(game: OnlineGame): string {
  return GAME_LABELS[game];
}
